import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import AltRouteIcon from "@mui/icons-material/AltRoute";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import ChatOutlinedIcon from "@mui/icons-material/ChatOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { fetchData } from "../../../api/client";
import { useUsers } from "../../../hooks/useUsers";

const emptyChoice = () => ({ action: "", targetUserId: "" });

const GROUP_META = {
  leads_open: { Icon: ForumOutlinedIcon, tone: "primary" },
  tasks_open: { Icon: AssignmentOutlinedIcon, tone: "secondary" },
  agenda_upcoming: { Icon: EventOutlinedIcon, tone: "info" },
  channels: { Icon: AltRouteIcon, tone: "warning" },
  automation_rules: { Icon: AccountTreeOutlinedIcon, tone: "success" },
  agent_distributions: { Icon: SmartToyOutlinedIcon, tone: "secondary" },
  chatbot_steps: { Icon: ChatOutlinedIcon, tone: "info" },
  chatbot_owned: { Icon: ChatOutlinedIcon, tone: "warning" },
  shared_agendas: { Icon: CalendarMonthOutlinedIcon, tone: "primary" },
};

function unwrapImpact(raw) {
  if (!raw || typeof raw !== "object") return null;
  if (Array.isArray(raw.groups) || Array.isArray(raw.history)) return raw;
  const inner = raw.data;
  if (inner && typeof inner === "object" && (Array.isArray(inner.groups) || Array.isArray(inner.history))) {
    return inner;
  }
  return null;
}

function choiceReady(actions, choice) {
  if (!choice?.action) return false;
  const action = (actions || []).find((item) => item.id === choice.action);
  if (!action) return false;
  return !action.needsTarget || Boolean(choice.targetUserId);
}

function DecisionControls({ idPrefix, actions, choice, users, onChange, compact = false }) {
  const action = (actions || []).find((item) => item.id === choice.action);
  return (
    <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ minWidth: compact ? 220 : 0, flex: compact ? "0 0 280px" : 1 }}>
      <FormControl size="small" fullWidth>
        <InputLabel id={`${idPrefix}-action`}>O que fazer</InputLabel>
        <Select
          labelId={`${idPrefix}-action`}
          label="O que fazer"
          value={choice.action}
          onChange={(event) => onChange({ action: event.target.value, targetUserId: "" })}
        >
          {(actions || []).map((item) => (
            <MenuItem key={item.id} value={item.id}>
              {item.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {action?.needsTarget ? (
        <FormControl size="small" fullWidth>
          <InputLabel id={`${idPrefix}-user`}>Usuário</InputLabel>
          <Select
            labelId={`${idPrefix}-user`}
            label="Usuário"
            value={choice.targetUserId}
            onChange={(event) => onChange({ targetUserId: event.target.value })}
          >
            {users.map((row) => (
              <MenuItem key={row.id} value={String(row.id)}>
                {row.nome || row.name || `Usuário #${row.id}`}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ) : null}
    </Stack>
  );
}

function GroupCard({ group, state, users, onChange }) {
  const theme = useTheme();
  const meta = GROUP_META[group.key] || { Icon: ForumOutlinedIcon, tone: "primary" };
  const tone = theme.palette[meta.tone]?.main || theme.palette.primary.main;
  const Icon = meta.Icon;
  const items = group.items || [];
  const selectedIds = Object.keys(state.selected || {}).filter((id) => state.selected[id]);
  const defined = items.filter((item) => choiceReady(group.actions, state.items?.[String(item.id)])).length;
  const allChecked = items.length > 0 && selectedIds.length === items.length;

  const applyTo = (ids) => {
    if (!choiceReady(group.actions, state.draft)) return;
    const nextItems = { ...(state.items || {}) };
    ids.forEach((id) => {
      nextItems[String(id)] = { ...state.draft };
    });
    onChange({
      ...state,
      items: nextItems,
      selected: {},
      coverRemainder: ids.length === items.length ? { ...state.draft } : state.coverRemainder,
    });
  };

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: alpha(tone, 0.35),
        borderRadius: 2,
        overflow: "hidden",
        bgcolor: alpha(tone, 0.04),
      }}
    >
      <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ px: 2, py: 1.5, bgcolor: alpha(tone, 0.1) }}>
        <Avatar sx={{ bgcolor: alpha(tone, 0.16), color: tone, width: 36, height: 36 }}>
          <Icon fontSize="small" />
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
              {group.label}
            </Typography>
            <Chip size="small" label={group.total} sx={{ fontWeight: 700, bgcolor: alpha(tone, 0.16), color: tone }} />
            <Typography variant="caption" sx={{ fontStyle: "italic", color: "text.secondary" }}>
              {defined} de {items.length} definidos
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            {group.hint}
          </Typography>
        </Box>
      </Stack>

      <Box sx={{ px: 2, py: 1.5, bgcolor: "background.paper", borderTop: "1px solid", borderColor: "divider" }}>
        <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.3, color: "text.secondary" }}>
          APLICAR EM LOTE
        </Typography>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems={{ md: "center" }} sx={{ mt: 1 }}>
          <DecisionControls
            idPrefix={`${group.key}-draft`}
            actions={group.actions}
            choice={state.draft || emptyChoice()}
            users={users}
            onChange={(patch) => onChange({ ...state, draft: { ...(state.draft || emptyChoice()), ...patch } })}
          />
          <Button
            size="small"
            variant="contained"
            disabled={!choiceReady(group.actions, state.draft)}
            onClick={() => applyTo(items.map((item) => item.id))}
          >
            Todos
          </Button>
          <Button
            size="small"
            variant="outlined"
            disabled={!selectedIds.length || !choiceReady(group.actions, state.draft)}
            onClick={() => applyTo(selectedIds)}
          >
            Selecionados ({selectedIds.length})
          </Button>
        </Stack>
      </Box>

      <Stack sx={{ maxHeight: 320, overflow: "auto", bgcolor: "background.paper" }}>
        {items.map((item, index) => {
          const id = String(item.id);
          const itemChoice = state.items?.[id] || emptyChoice();
          const ready = choiceReady(group.actions, itemChoice);
          const action = (group.actions || []).find((entry) => entry.id === itemChoice.action);
          return (
            <Stack
              key={id}
              direction={{ xs: "column", md: "row" }}
              spacing={1}
              alignItems={{ md: "center" }}
              sx={{
                px: 1.5,
                py: 1,
                borderTop: "1px solid",
                borderColor: "divider",
                bgcolor: state.selected?.[id] ? alpha(tone, 0.08) : index % 2 ? "action.hover" : "background.paper",
              }}
            >
              <Checkbox
                size="small"
                checked={Boolean(state.selected?.[id])}
                onChange={(event) =>
                  onChange({
                    ...state,
                    selected: { ...(state.selected || {}), [id]: event.target.checked },
                  })
                }
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                  {item.label}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  #{item.id}
                </Typography>
              </Box>
              <Chip
                size="small"
                variant={ready ? "filled" : "outlined"}
                color={ready ? "success" : "warning"}
                label={ready ? action?.label || itemChoice.action : "Pendente"}
                sx={{ maxWidth: 220, "& .MuiChip-label": { overflow: "hidden", textOverflow: "ellipsis" } }}
              />
              <DecisionControls
                compact
                idPrefix={`${group.key}-${id}`}
                actions={group.actions}
                choice={itemChoice}
                users={users}
                onChange={(patch) =>
                  onChange({
                    ...state,
                    items: { ...(state.items || {}), [id]: { ...itemChoice, ...patch } },
                  })
                }
              />
            </Stack>
          );
        })}
        {group.total > items.length ? (
          <Typography variant="caption" color="text.secondary" sx={{ px: 2, py: 1, fontStyle: "italic" }}>
            Há mais {group.total - items.length} item(ns) além desta lista. “Todos” também cobre o restante.
          </Typography>
        ) : null}
      </Stack>

      {items.length > 0 ? (
        <Stack direction="row" alignItems="center" sx={{ px: 1, py: 0.5, borderTop: "1px solid", borderColor: "divider", bgcolor: "background.paper" }}>
          <Checkbox
            size="small"
            checked={allChecked}
            indeterminate={selectedIds.length > 0 && !allChecked}
            onChange={(event) => {
              const selected = {};
              if (event.target.checked) items.forEach((item) => {
                selected[String(item.id)] = true;
              });
              onChange({ ...state, selected });
            }}
          />
          <Typography variant="caption" color="text.secondary">
            Selecionar todos deste bloco
          </Typography>
        </Stack>
      ) : null}
    </Box>
  );
}

export default function UserDeletionDialog({
  open,
  user,
  candidates,
  submitting = false,
  errorMessage = "",
  title = "Excluir usuário",
  confirmLabel = "Excluir usuário",
  onClose,
  onConfirm,
}) {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [impact, setImpact] = useState(null);
  const [groupsState, setGroupsState] = useState({});

  useEffect(() => {
    if (!open || !user?.id) return undefined;
    let cancelled = false;
    setLoading(true);
    setLoadError("");
    setImpact(null);
    setGroupsState({});
    fetchData(`/users/${user.id}/deletion-impact`)
      .then((data) => {
        if (cancelled) return;
        const impactPayload = unwrapImpact(data);
        if (!impactPayload) {
          setLoadError("Não foi possível ler os vínculos deste usuário.");
          setImpact(null);
          return;
        }
        setImpact(impactPayload);
        const next = {};
        for (const group of impactPayload.groups || []) {
          next[group.key] = { draft: emptyChoice(), selected: {}, items: {}, coverRemainder: null };
        }
        setGroupsState(next);
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadError(err?.response?.data?.message || "Não foi possível listar os vínculos deste usuário.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, user?.id]);

  const { data: directory } = useUsers(1, 1000, "", "humanos", {
    enabled: open,
    order: "nome",
  });
  const others = useMemo(() => {
    const rows = candidates?.length ? candidates : directory?.users || [];
    return rows.filter((row) => String(row.id) !== String(user?.id) && Number(row.excluido) !== 1);
  }, [candidates, directory?.users, user?.id]);

  const pending = (impact?.groups || []).some((group) => {
    const state = groupsState[group.key] || {};
    const items = group.items || [];
    const missingItem = items.some((item) => !choiceReady(group.actions, state.items?.[String(item.id)]));
    const remainder = group.total > items.length && !choiceReady(group.actions, state.coverRemainder);
    return missingItem || remainder;
  });

  const definedCount = (impact?.groups || []).reduce((sum, group) => {
    const state = groupsState[group.key] || {};
    return sum + (group.items || []).filter((item) => choiceReady(group.actions, state.items?.[String(item.id)])).length;
  }, 0);
  const listedCount = (impact?.groups || []).reduce((sum, group) => sum + (group.items || []).length, 0);

  return (
    <Dialog open={open} onClose={() => !submitting && onClose?.()} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Avatar sx={{ bgcolor: alpha(theme.palette.error.main, 0.12), color: "error.main" }}>
            <PersonOffOutlinedIcon />
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {user?.nome ? (
                <>
                  Destino das demandas de <strong>{user.nome}</strong>
                </>
              ) : (
                "Destino das demandas em aberto"
              )}
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: "grey.50", px: { xs: 1.5, sm: 2.5 } }}>
        {loading && (
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 3 }}>
            <CircularProgress size={22} />
            <Typography variant="body2">Listando vínculos em aberto…</Typography>
          </Stack>
        )}

        {!loading && (
          <Stack spacing={2} sx={{ py: 0.5 }}>
            {(loadError || errorMessage) && <Alert severity="error">{errorMessage || loadError}</Alert>}

            {impact?.historyNote && (
              <Alert severity="info" icon={<HistoryOutlinedIcon />}>
                {impact.historyNote}
              </Alert>
            )}

            {(impact?.groups || []).length === 0 && !loadError && (
              <Alert severity="success">Não há demandas em aberto vinculadas a esta conta.</Alert>
            )}

            {listedCount > 0 && (
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {definedCount} de {listedCount} itens com destino definido
              </Typography>
            )}

            {(impact?.groups || []).map((group) => (
              <GroupCard
                key={group.key}
                group={group}
                users={others}
                state={groupsState[group.key] || { draft: emptyChoice(), selected: {}, items: {} }}
                onChange={(next) => setGroupsState((prev) => ({ ...prev, [group.key]: next }))}
              />
            ))}

            {(impact?.history || []).length > 0 && (
              <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, overflow: "hidden", bgcolor: "background.paper" }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 2, py: 1.25, bgcolor: "action.hover" }}>
                  <HistoryOutlinedIcon fontSize="small" color="action" />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Permanecem no histórico
                  </Typography>
                </Stack>
                <Stack direction="row" flexWrap="wrap" gap={1} sx={{ p: 2 }}>
                  {(impact.history || []).map((item) => (
                    <Chip key={item.key} variant="outlined" label={`${item.label}: ${item.total}`} />
                  ))}
                </Stack>
              </Box>
            )}

            {(impact?.automatic || []).length > 0 && (
              <Box sx={{ border: "1px dashed", borderColor: "divider", borderRadius: 2, p: 2, bgcolor: "background.paper" }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <InfoOutlinedIcon fontSize="small" color="action" />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Acontece automaticamente
                  </Typography>
                </Stack>
                {(impact.automatic || []).map((note) => (
                  <Typography key={note} variant="body2" color="text.secondary" sx={{ fontStyle: "italic", mb: 0.5 }}>
                    {note}
                  </Typography>
                ))}
              </Box>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose} disabled={submitting}>
          Cancelar
        </Button>
        <Button
          color="error"
          variant="contained"
          disabled={loading || submitting || Boolean(loadError) || pending}
          onClick={() => {
            const resolutions = {};
            for (const group of impact?.groups || []) {
              const state = groupsState[group.key] || {};
              const items = {};
              for (const item of group.items || []) {
                const choice = state.items?.[String(item.id)];
                if (!choice?.action) continue;
                items[String(item.id)] = {
                  action: choice.action,
                  targetUserId: choice.targetUserId ? Number(choice.targetUserId) : null,
                };
              }
              const cover = state.coverRemainder;
              resolutions[group.key] = {
                action: cover?.action || "",
                targetUserId: cover?.targetUserId ? Number(cover.targetUserId) : null,
                items,
              };
            }
            onConfirm?.(resolutions);
          }}
        >
          {submitting ? "Excluindo…" : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
